import type { Logger } from '@vp/ubik-logging';
import type { Identity } from '../../auth/useOnUserIdentityUpdate';
import type { IndustryKey } from './IndustryTabsHeaders';

export interface GalleryRequestConfig {
    companyName: string;
    industryName: string;
    industryKeywords: string[];
}

export type GalleryExamples = {
    [key in IndustryKey]: LogomakerLogo[];
};

export interface LogomakerLogo {
    logo: string;
    signature: {
        logoComposerId: string;
        companyName?: {
            font?: {
                default?: {
                    style?: string;
                };
            };
        };
        backgroundColor?: {
            default?: string;
        };
        layout?: {
            default?: string;
        };
    };
}

export async function* getGalleryLogoExamples(
    requestBody: GalleryRequestConfig,
    identity: Identity,
    sessionId: string,
    abortController: AbortController,
    trackApiError: (requestBody: GalleryRequestConfig) => void,
    logger: Logger,
    logomationBaseUrl: string,
    isRetry = false,
): AsyncGenerator<LogomakerLogo> {
    // snyk:ignore=SSRF
    // logomationBaseUrl is environment-controlled and not user-controlled.
    const url = `${logomationBaseUrl}/v1/ideas/generate/stream/?singleLayout=false&compressFile=true&fullSignature=true&tenant=logomaker_anywhere`;
    // Fetch designs from a random page number between 1 - 5
    // so that user does not see same logo designs on reload
    const pageNo = Math.floor(Math.random() * 5) + 1;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: identity.authorizationHeader,
                Ssid: sessionId,
            },
            body: JSON.stringify({
                request: {
                    companyName: requestBody.companyName,
                    tagline: '',
                    industryName: requestBody.industryName,
                    industryKeywords: requestBody.industryKeywords,
                    pageNo,
                    galleryFilter: {
                        graphic: ['icon'],
                        layout: ['stackedV3_1', 'circular_1', 'archIconLayout'],
                        fontPair: [],
                    },
                },
                shopperId: identity.canonicalId,
            }),
            signal: abortController.signal,
        });

        if (response.body) {
            const reader = response.body.getReader();
            let done = false;
            let value: Uint8Array | undefined;
            let runningText = '';

            while (!done) {
                ({ done, value } = await reader.read());
                if (done) {
                    break;
                }

                const text = new TextDecoder('utf-8').decode(value);
                const lines = text.split('\n');
                for (const line of lines) {
                    try {
                        runningText += line;
                        yield JSON.parse(runningText);
                        runningText = '';
                    } catch {
                        // Skip error and process next chunk as we don't have a full JSON object yet
                    }
                }
            }
        }
    } catch (error) {
        if ((error as Error).name === 'AbortError') {
            return [];
        }

        if (isRetry) {
            logger.error({
                name: 'Logomaker Anywhere: Failed to fetch logo examples from logomation api',
                message: (error as Error).message,
                logData: {
                    identity,
                    companyName: requestBody.companyName,
                    keyword: requestBody.industryKeywords,
                },
            });

            trackApiError(requestBody);

            return [];
        }

        return getGalleryLogoExamples(
            requestBody,
            identity,
            sessionId,
            abortController,
            trackApiError,
            logger,
            logomationBaseUrl,
            true,
        );
    }
}
